import { useMemo } from 'react'
import {
	MessageSquare,
	Image,
	Video,
	Mic,
	FileText,
	MapPin,
	Smartphone,
} from 'lucide-react'
import { render as renderFlexMessage } from 'flex-render'
import type { FlexContainer } from 'flex-render'

import { cn } from '@/common/lib/utils'

import { ChatMessageType } from '@/schema'
import { getStickerPreviewUrl } from '../../../../components/StickerPicker'

import { getMessageTypeConfig } from '../../types'
import type { MessageContentInput } from '../../types'

interface MessagePreviewProps {
	message: MessageContentInput
	className?: string
}

export function MessagePreview({ message, className }: MessagePreviewProps) {
	const typeConfig = getMessageTypeConfig(message.messageType)

	return (
		<div
			className={cn(
				'overflow-hidden rounded-lg border bg-card shadow-sm',
				className,
			)}
		>
			<div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
				<span className="text-sm">{typeConfig?.icon}</span>
				<span className="text-xs font-medium text-muted-foreground">
					{typeConfig?.label}
				</span>
			</div>
			<div className="p-3">{renderPreviewContent(message)}</div>
		</div>
	)
}

function renderPreviewContent(message: MessageContentInput) {
	switch (message.messageType) {
		case ChatMessageType.TEXT:
			return (
				<div className="rounded-lg bg-primary/10 px-3 py-2">
					<p className="whitespace-pre-wrap text-sm">
						{message.text || (
							<span className="italic text-muted-foreground">ไม่มีข้อความ</span>
						)}
					</p>
				</div>
			)

		case ChatMessageType.STICKER:
			if (message.stickerPackageId && message.stickerId) {
				return (
					<div className="flex justify-center">
						<img
							src={getStickerPreviewUrl(message.stickerId)}
							alt="Sticker"
							className="h-24 w-24 object-contain"
						/>
					</div>
				)
			}
			return (
				<div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
					<span className="text-2xl">😀</span>
					<span className="text-sm italic">ยังไม่ได้เลือกสติกเกอร์</span>
				</div>
			)

		case ChatMessageType.IMAGE:
			if (message.imageUrl) {
				return (
					<div className="overflow-hidden rounded-lg">
						<img
							src={message.imageUrl}
							alt="Preview"
							className="h-auto max-h-48 w-full object-contain"
						/>
					</div>
				)
			}
			return (
				<div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
					<Image className="h-8 w-8" />
					<span className="text-sm italic">ยังไม่ได้อัพโหลดรูปภาพ</span>
				</div>
			)

		case ChatMessageType.VIDEO:
			if (message.videoUrl) {
				return (
					<div className="relative overflow-hidden rounded-lg bg-black">
						{message.videoPreviewUrl ? (
							<img
								src={message.videoPreviewUrl}
								alt="Video preview"
								className="h-auto max-h-48 w-full object-contain"
							/>
						) : (
							<div className="flex h-32 items-center justify-center">
								<Video className="h-12 w-12 text-white/50" />
							</div>
						)}
						<div className="absolute inset-0 flex items-center justify-center bg-black/30">
							<div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
								<div className="border-l-12 ml-1 h-0 w-0 border-y-8 border-y-transparent border-l-white" />
							</div>
						</div>
					</div>
				)
			}
			return (
				<div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
					<Video className="h-8 w-8" />
					<span className="text-sm italic">ยังไม่ได้อัพโหลดวิดีโอ</span>
				</div>
			)

		case ChatMessageType.AUDIO:
			if (message.audioUrl) {
				return (
					<div className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
						<div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary">
							<Mic className="h-5 w-5 text-primary-foreground" />
						</div>
						<div className="flex-1">
							<div className="h-1 rounded-full bg-muted-foreground/30">
								<div className="h-full w-1/3 rounded-full bg-primary" />
							</div>
							<p className="mt-1 text-xs text-muted-foreground">
								{message.audioDuration
									? formatDuration(message.audioDuration)
									: 'ไม่ระบุระยะเวลา'}
							</p>
						</div>
					</div>
				)
			}
			return (
				<div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
					<Mic className="h-8 w-8" />
					<span className="text-sm italic">ยังไม่ได้อัพโหลดไฟล์เสียง</span>
				</div>
			)

		case ChatMessageType.FILE:
			if (message.fileUrl) {
				return (
					<div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
						<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
							<FileText className="h-5 w-5" />
						</div>
						<div className="flex-1 overflow-hidden">
							<p className="truncate text-sm font-medium">
								{message.fileName || 'ไฟล์'}
							</p>
							<p className="truncate text-xs text-muted-foreground">
								{message.fileUrl}
							</p>
						</div>
					</div>
				)
			}
			return (
				<div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
					<FileText className="h-8 w-8" />
					<span className="text-sm italic">ยังไม่ได้อัพโหลดไฟล์</span>
				</div>
			)

		case ChatMessageType.LOCATION:
			if (
				message.locationLatitude !== undefined &&
				message.locationLongitude !== undefined
			) {
				return (
					<div className="space-y-2">
						<div className="relative h-32 overflow-hidden rounded-lg bg-muted">
							{/* Static map placeholder */}
							<div className="flex h-full items-center justify-center bg-gradient-to-b from-green-100 to-green-200">
								<MapPin className="h-8 w-8 text-red-500" />
							</div>
						</div>
						<div className="space-y-1">
							{message.locationTitle && (
								<p className="text-sm font-medium">{message.locationTitle}</p>
							)}
							{message.locationAddress && (
								<p className="text-xs text-muted-foreground">
									{message.locationAddress}
								</p>
							)}
							<p className="text-xs text-muted-foreground">
								{message.locationLatitude.toFixed(6)},{' '}
								{message.locationLongitude.toFixed(6)}
							</p>
						</div>
					</div>
				)
			}
			return (
				<div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
					<MapPin className="h-8 w-8" />
					<span className="text-sm italic">ยังไม่ได้ระบุตำแหน่ง</span>
				</div>
			)

		case ChatMessageType.FLEX:
			return <FlexMessagePreview message={message} />

		default:
			return (
				<div className="flex items-center justify-center gap-2 py-4 text-muted-foreground">
					<MessageSquare className="h-5 w-5" />
					<span className="text-sm italic">ไม่รองรับประเภทข้อความนี้</span>
				</div>
			)
	}
}

// ==================== Flex Message Preview ====================

function FlexMessagePreview({ message }: { message: MessageContentInput }) {
	const renderedHtml = useMemo(() => {
		if (!message.flexContents) return null
		try {
			return renderFlexMessage(message.flexContents as FlexContainer)
		} catch {
			return null
		}
	}, [message.flexContents])

	if (!message.flexContents) {
		return (
			<div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
				<Smartphone className="h-8 w-8" />
				<span className="text-sm italic">ยังไม่ได้กำหนด Flex Message</span>
			</div>
		)
	}

	if (renderedHtml) {
		return (
			<div className="space-y-2">
				<p className="text-xs text-muted-foreground">
					Alt: {message.flexAltText || 'ไม่มี'}
				</p>
				<div
					className="flex-preview overflow-hidden"
					dangerouslySetInnerHTML={{ __html: renderedHtml }}
				/>
			</div>
		)
	}

	// Fallback if render fails
	return (
		<div className="space-y-2">
			<div className="rounded-lg border bg-gradient-to-br from-purple-50 to-blue-50 p-4">
				<div className="flex items-center gap-2">
					<Smartphone className="h-5 w-5 text-purple-500" />
					<span className="text-sm font-medium text-purple-700">
						Flex Message
					</span>
				</div>
				<p className="mt-2 text-xs text-muted-foreground">
					Alt: {message.flexAltText || 'ไม่มี'}
				</p>
			</div>
			<details className="group">
				<summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
					ดู JSON
				</summary>
				<pre className="mt-2 max-h-48 overflow-auto rounded bg-muted p-2 text-xs">
					{JSON.stringify(message.flexContents, null, 2)}
				</pre>
			</details>
		</div>
	)
}

// ==================== Helpers ====================

function formatDuration(ms: number): string {
	const seconds = Math.floor(ms / 1000)
	const minutes = Math.floor(seconds / 60)
	const remainingSeconds = seconds % 60

	if (minutes > 0) {
		return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
	}
	return `0:${remainingSeconds.toString().padStart(2, '0')}`
}

// ==================== Message List Preview ====================

interface MessageListPreviewProps {
	messages: MessageContentInput[]
	className?: string
}

export function MessageListPreview({
	messages,
	className,
}: MessageListPreviewProps) {
	if (messages.length === 0) {
		return (
			<div
				className={cn(
					'rounded-lg border border-dashed p-8 text-center',
					className,
				)}
			>
				<p className="text-sm text-muted-foreground">ไม่มีข้อความ</p>
			</div>
		)
	}

	return (
		<div className={cn('space-y-3', className)}>
			{messages.map((message, index) => (
				<MessagePreview
					key={message.id || index}
					message={message}
					className="transition-all hover:shadow-md"
				/>
			))}
		</div>
	)
}
